/**
 * Broker Mappings Tab Component
 * 
 * Displays broker-specific symbol mappings in a comparison/timeline view.
 * Features:
 * - Search for instruments by symbol (shows ALL results)
 * - Filter by instrument type and expiry date
 * - Select an instrument to view its reconciled details
 * - Compare broker-specific mappings in a timeline view
 * - View raw source data from each broker
 * - Identify which broker is the primary source
 * 
 * @module InstrumentMaster/tabs/BrokerMappingsTab
 */

import React, { useState, useMemo } from 'react';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { FiSearch, FiChevronRight, FiCheck, FiDatabase, FiLayers, FiX, FiClock, FiFilter, FiCalendar, FiArrowLeft, FiMoreHorizontal, FiInfo } from 'react-icons/fi';

const BrokerMappingsTab = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [searchSymbol, setSearchSymbol] = useState('');
  
  // Search results - ALL instruments
  const [searchResults, setSearchResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  
  // Filters for drill-down
  const [filterInstrumentType, setFilterInstrumentType] = useState('');
  const [filterExpiry, setFilterExpiry] = useState('');
  
  // Selected instrument details
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  
  // Expanded source data panels
  const [expandedBrokers, setExpandedBrokers] = useState({});

  const toggleBrokerExpanded = (brokerId) => {
    setExpandedBrokers((prev) => ({ ...prev, [brokerId]: !prev[brokerId] }));
  };

  // Toggle for full instrument details (Metadata & Raw)
  const [showFullDetails, setShowFullDetails] = useState(false);

  // Get unique instrument types and expiry dates for filter dropdowns
  const { uniqueTypes, uniqueExpiries } = useMemo(() => {
    const types = new Set();
    const expiries = new Set();
    
    searchResults.forEach(inst => {
      if (inst.instrument_type) types.add(inst.instrument_type);
      if (inst.expiry_date) {
        // Format expiry date for display
        const expDate = new Date(inst.expiry_date).toLocaleDateString();
        expiries.add(expDate);
      }
    });
    
    return {
      uniqueTypes: Array.from(types).sort(),
      uniqueExpiries: Array.from(expiries).sort((a, b) => new Date(a) - new Date(b))
    };
  }, [searchResults]);

  // Filtered results based on selected filters
  const filteredResults = useMemo(() => {
    let results = searchResults;
    
    if (filterInstrumentType) {
      results = results.filter(inst => inst.instrument_type === filterInstrumentType);
    }
    
    if (filterExpiry) {
      results = results.filter(inst => {
        if (!inst.expiry_date) return false;
        const expDate = new Date(inst.expiry_date).toLocaleDateString();
        return expDate === filterExpiry;
      });
    }
    
    return results;
  }, [searchResults, filterInstrumentType, filterExpiry]);

  // Search for instruments (NO LIMIT - get all)
  const searchInstruments = async () => {
    if (!searchSymbol.trim()) {
      showSnackbar('Please enter a symbol to search', 'warning');
      return;
    }

    setLoading(true);
    setSelectedInstrument(null);
    setMappings([]);
    setFilterInstrumentType('');
    setFilterExpiry('');
    
    try {
      // Request up to 1000 results to get ALL matching instruments
      const searchResponse = await services.instruments.searchInstruments({
        symbol: searchSymbol,
        limit: 1000,
      });

      console.debug("[BrokerMappingsTab] Search response:", searchResponse.data);

      if (searchResponse.data.instruments && searchResponse.data.instruments.length > 0) {
        setSearchResults(searchResponse.data.instruments);
        setTotalResults(searchResponse.data.total || searchResponse.data.instruments.length);
        showSnackbar(`Found ${searchResponse.data.instruments.length} instruments`, 'success');
      } else {
        setSearchResults([]);
        setTotalResults(0);
        showSnackbar('No instruments found', 'warning');
      }
    } catch (error) {
      console.error("[BrokerMappingsTab] Search error:", error);
      showSnackbar(error.message || 'Failed to search instruments', 'error');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch mappings for selected instrument
  const selectInstrument = async (instrument) => {
    setSelectedInstrument(instrument);
    setLoadingMappings(true);
    setExpandedBrokers({});
    
    try {
      console.debug("[BrokerMappingsTab] Fetching mappings for:", instrument.instrument_id);
      const mappingsResponse = await services.instruments.getInstrumentMappings(instrument.instrument_id);
      console.debug("[BrokerMappingsTab] Mappings response:", mappingsResponse);

      // Normalize response shape
      const respData = mappingsResponse?.data;
      let mappingsData = [];
      
      if (Array.isArray(respData)) {
        mappingsData = respData;
      } else if (respData) {
        mappingsData = respData.broker_mappings || respData.mappings || respData.data || [];
      }

      console.debug("[BrokerMappingsTab] Parsed mappings:", mappingsData.length, "brokers");
      setMappings(mappingsData);
      
      if (mappingsData.length === 0) {
        showSnackbar('No broker mappings found for this instrument. Check if sync has run for all brokers.', 'warning');
      } else {
        showSnackbar(`Found ${mappingsData.length} broker mapping(s)`, 'success');
      }
    } catch (error) {
      console.error("[BrokerMappingsTab] Mappings error:", error);
      showSnackbar(error.message || 'Failed to fetch broker mappings', 'error');
      setMappings([]);
    } finally {
      setLoadingMappings(false);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedInstrument(null);
    setMappings([]);
    setExpandedBrokers({});
    setShowFullDetails(false);
  };

  // Clear filters
  const clearFilters = () => {
    setFilterInstrumentType('');
    setFilterExpiry('');
  };

  // Format expiry date for display
  const formatExpiry = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <div className="bg-bg-3/50 p-4 rounded-lg">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Search Instrument by Symbol
            </label>
            <input
              type="text"
              value={searchSymbol}
              onChange={(e) => setSearchSymbol(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchInstruments()}
              placeholder="Enter symbol (e.g., RELIANCE, NIFTY, TCS, HDFCBANK)"
              className="w-full px-3 py-2 border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={searchInstruments}
              disabled={loading}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FiSearch />
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </div>

      {/* Search Results List with Filters */}
      {searchResults.length > 0 && !selectedInstrument && (
        <div className="bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)]">
          {/* Header with count */}
          <div className="px-4 py-3 border-b border-[var(--divider)] bg-bg-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="ds-h3 text-fg-1">
                  Search Results ({totalResults} total, showing {filteredResults.length})
                </h3>
                <p className="text-sm text-fg-2 mt-1">
                  Click on an instrument to view broker-specific mappings and source data
                </p>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="px-4 py-3 border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/50">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <FiFilter className="text-fg-2" size={16} />
                <span className="text-sm font-medium text-fg-2">Filter by:</span>
              </div>
              
              {/* Instrument Type Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-fg-2">Type:</label>
                <select
                  value={filterInstrumentType}
                  onChange={(e) => setFilterInstrumentType(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
                >
                  <option value="">All Types ({uniqueTypes.length})</option>
                  {uniqueTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Expiry Date Filter */}
              {uniqueExpiries.length > 0 && (
                <div className="flex items-center gap-2">
                  <FiCalendar className="text-fg-2" size={14} />
                  <label className="text-sm text-fg-2">Expiry:</label>
                  <select
                    value={filterExpiry}
                    onChange={(e) => setFilterExpiry(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
                  >
                    <option value="">All Expiries ({uniqueExpiries.length})</option>
                    {uniqueExpiries.map(exp => (
                      <option key={exp} value={exp}>{exp}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Clear Filters */}
              {(filterInstrumentType || filterExpiry) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="btn btn-danger btn-sm flex items-center gap-1"
                >
                  <FiX size={14} />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Results List */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[500px] overflow-y-auto">
            {filteredResults.length === 0 ? (
              <div className="px-4 py-8 text-center text-fg-2">
                No instruments match the selected filters.
              </div>
            ) : (
              filteredResults.map((instrument) => (
                <button
                  key={instrument.instrument_id}
                  onClick={() => selectInstrument(instrument)}
                  className="w-full px-4 py-3 text-left hover:bg-bg-3 transition-colors flex items-center justify-between group"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-fg-1">
                        {instrument.symbol}
                      </span>
                      <span className="ds-badge neutral xs">
                        {instrument.exchange}
                      </span>
                      <span className="ds-badge neutral xs">
                        {instrument.instrument_type}
                      </span>
                      {instrument.expiry_date && (
                        <span className="ds-badge neutral xs">
                          <FiCalendar size={10} />
                          {formatExpiry(instrument.expiry_date)}
                        </span>
                      )}
                      {instrument.strike_price && (
                        <span className="ds-badge neutral xs">
                          ₹{instrument.strike_price}
                        </span>
                      )}
                      {instrument.option_type && (
                        <span className={`ds-badge xs ${instrument.option_type === 'CE' ? 'profit' : 'loss'}`}>
                          {instrument.option_type}
                        </span>
                      )}

                      {!instrument.is_tradable && (
                        <span className="ds-badge warn xs" title="Limited trading data / Low liquidity">
                          Limited
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-fg-2 mt-1">
                      {instrument.name}
                    </div>
                    <div className="text-xs text-fg-3 mt-0.5 font-mono">
                      ISIN: {instrument.isin || 'N/A'} | ID: {instrument.instrument_id?.slice(0, 8)}...
                    </div>
                  </div>
                  <FiChevronRight className="text-fg-3 group-hover:text-blue-600 transition-colors" size={20} />
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Selected Instrument - Timeline/Comparison View */}
      {selectedInstrument && (
        <div className="space-y-6">
          {/* Back button + Instrument Header */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="btn btn-ghost btn-sm flex items-center gap-1"
                  >
                    <FiArrowLeft size={16} /> Back to results
                  </button>
                </div>
                <h2 className="ds-h2 text-fg-1 flex items-center gap-2">
                  <FiDatabase className="text-blue-600" />
                  {selectedInstrument.name}
                  {!selectedInstrument.is_tradable && (
                    <span className="ds-badge warn sm ml-2">
                      Limited Trading
                    </span>
                  )}
                </h2>
                <p className="text-fg-2 mt-1">
                  Reconciled Instrument Details (Final Master Record)
                </p>
              </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFullDetails(!showFullDetails)}
                    className="px-3 py-1 bg-bg-2 border border-[var(--divider)] text-fg-2 rounded-md text-sm font-medium flex items-center gap-1 hover:bg-bg-3 transition-colors"
                  >
                    {showFullDetails ? <FiChevronRight className="rotate-90" /> : <FiMoreHorizontal />}
                    {showFullDetails ? 'Hide Details' : 'Show Full Details'}
                  </button>
                  <span className="ds-badge profit sm">
                    <FiCheck size={14} /> Master Record
                  </span>
                </div>
            </div>

            {/* Reconciled Instrument Details Grid */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                <div className="text-[10px] text-fg-2 uppercase tracking-wide">Symbol</div>
                <div className="text-base font-bold text-fg-1 truncate" title={selectedInstrument.symbol}>{selectedInstrument.symbol}</div>
              </div>
              <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                <div className="text-[10px] text-fg-2 uppercase tracking-wide">Exchange</div>
                <div className="text-base font-bold text-fg-1">{selectedInstrument.exchange}</div>
              </div>
              <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                <div className="text-[10px] text-fg-2 uppercase tracking-wide">Type</div>
                <div className="text-base font-bold text-fg-1">{selectedInstrument.instrument_type}</div>
              </div>
              
              {/* Conditional Fields */}
              {selectedInstrument.expiry_date && (
                <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                  <div className="text-[10px] text-fg-2 uppercase tracking-wide">Expiry</div>
                  <div className="text-base font-bold text-fg-1">{formatExpiry(selectedInstrument.expiry_date)}</div>
                </div>
              )}
              
              {(selectedInstrument.option_type || selectedInstrument.strike_price) && (
                <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                  <div className="text-[10px] text-fg-2 uppercase tracking-wide">Option</div>
                  <div className="text-base font-bold text-fg-1 flex items-baseline gap-1">
                    {selectedInstrument.strike_price} 
                    <span className={`text-xs ${selectedInstrument.option_type === 'CE' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedInstrument.option_type}
                    </span>
                  </div>
                </div>
              )}

              {selectedInstrument.group_classification && (
                <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                  <div className="text-[10px] text-fg-2 uppercase tracking-wide">Group</div>
                  <div className="text-base font-bold text-fg-1 truncate" title={selectedInstrument.group_classification}>
                    {selectedInstrument.group_classification}
                  </div>
                </div>
              )}

              <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                <div className="text-[10px] text-fg-2 uppercase tracking-wide">ISIN</div>
                <div className="text-xs font-mono font-bold text-fg-1 truncate" title={selectedInstrument.isin}>
                  {selectedInstrument.isin || 'N/A'}
                </div>
              </div>
              <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                <div className="text-[10px] text-fg-2 uppercase tracking-wide">Lot Size</div>
                <div className="text-base font-bold text-fg-1">{selectedInstrument.lot_size || 1}</div>
              </div>
              <div className="bg-bg-2 rounded-lg p-2 shadow-sm border border-[var(--divider)]">
                <div className="text-[10px] text-fg-2 uppercase tracking-wide">Tick Size</div>
                <div className="text-base font-bold text-fg-1">{selectedInstrument.tick_size || 0.05}</div>
              </div>
            </div>

            {/* Expanded Full Details View */}
            {showFullDetails && (
              <div className="mt-4 pt-4 border-t border-[var(--divider)]">
                 <h4 className="flex items-center gap-2 text-sm font-semibold text-fg-1 mb-3">
                   <FiInfo className="text-blue-500" />
                   Extended Meta Data & Sync Information
                 </h4>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
                    {/* Timestamps & ID */}
                    <div className="space-y-2">
                       <div className="flex justify-between text-sm">
                         <span className="text-fg-2">Created At:</span>
                         <span className="font-mono text-fg-2">
                            {selectedInstrument.created_at ? new Date(selectedInstrument.created_at).toLocaleString() : 'N/A'}
                         </span>
                       </div>
                       <div className="flex justify-between text-sm">
                         <span className="text-fg-2">Last Updated:</span>
                         <span className="font-mono text-fg-2">
                            {selectedInstrument.updated_at ? new Date(selectedInstrument.updated_at).toLocaleString() : 'N/A'}
                         </span>
                       </div>
                       <div className="flex justify-between text-sm">
                         <span className="text-fg-2">Last Synced:</span>
                         <span className="font-mono text-fg-2">
                            {selectedInstrument.last_synced_at ? new Date(selectedInstrument.last_synced_at).toLocaleString() : 'N/A'}
                         </span>
                       </div>
                       <div className="flex justify-between text-sm">
                         <span className="text-fg-2">Updated By Broker ID:</span>
                         <span className="font-mono text-fg-1 bg-bg-3 px-1 rounded">
                            {selectedInstrument.last_updated_by_broker_id || 'N/A'}
                         </span>
                       </div>
                    </div>

                    {/* Broker Data Sources */}
                    <div className="col-span-1 md:col-span-2">
                      <h5 className="text-xs font-semibold text-fg-2 uppercase mb-2">Broker Data Sources</h5>
                      {/* overflow-x-auto below: the min-w-full table overflows a phone,
                          and overflow-hidden alone clipped the extra columns. */}
                      {selectedInstrument.broker_data_sources && selectedInstrument.broker_data_sources.broker_ids ? (
                         <div className="bg-bg-2 rounded border border-[var(--divider)] overflow-hidden overflow-x-auto">
                           <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                             <thead className="bg-bg-3 dark:bg-bg-3/50">
                               <tr>
                                 <th className="px-3 py-2 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">Broker</th>
                                 <th className="px-3 py-2 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">Status</th>
                                 <th className="px-3 py-2 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">Last Sync</th>
                               </tr>
                             </thead>
                             <tbody className="bg-bg-2 divide-y divide-gray-200 dark:divide-gray-700">
                               {selectedInstrument.broker_data_sources.broker_ids.map(brokerId => {
                                 const mapping = mappings.find(m => m.broker_id === brokerId);
                                 const brokerName = mapping ? mapping.broker_name : `Broker ID: ${brokerId}`;
                                 const lastSync = selectedInstrument.broker_data_sources.last_sync?.[brokerId];
                                 const isProcessed = mapping && mapping.processing_status === 'processed';
                                 
                                 return (
                                   <tr key={brokerId}>
                                     <td className="px-3 py-2 whitespace-nowrap text-sm text-fg-1 font-medium">
                                       {brokerName}
                                     </td>
                                     <td className="px-3 py-2 whitespace-nowrap text-xs">
                                       {isProcessed ? (
                                         <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><FiCheck size={10}/> Synced</span>
                                       ) : (
                                          <span className="text-fg-2">Available</span>
                                       )}
                                     </td>
                                     <td className="px-3 py-2 whitespace-nowrap text-xs text-fg-2 font-mono">
                                       {lastSync ? new Date(lastSync).toLocaleString() : '-'}
                                     </td>
                                   </tr>
                                 );
                               })}
                             </tbody>
                           </table>
                         </div>
                      ) : (
                         <span className="text-sm text-fg-3 italic">No source metadata available</span>
                      )}
                    </div>
                 </div>

                 {/* Full Raw Object Dump */}
                 <details>
                   <summary className="text-sm text-blue-600 cursor-pointer hover:underline mb-2">View Complete Raw Instrument Object</summary>
                   <div className="p-3 bg-gray-900 text-gray-100 rounded-md font-mono text-xs overflow-auto max-h-60">
                     <pre>{JSON.stringify(selectedInstrument, null, 2)}</pre>
                   </div>
                 </details>
              </div>
            )}
          </div>

          {/* Broker Mappings Timeline */}
          <div className="bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)]">
            <div className="px-4 py-3 border-b border-[var(--divider)] bg-bg-3 flex items-center justify-between">
              <div>
                <h3 className="ds-h3 text-fg-1 flex items-center gap-2">
                  <FiLayers className="text-indigo-600" />
                  Broker Source Data ({mappings.length} broker{mappings.length !== 1 ? 's' : ''})
                </h3>
                <p className="text-sm text-fg-2 mt-1">
                  Compare how each broker represents this instrument
                </p>
              </div>
              {loadingMappings && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  Loading...
                </div>
              )}
            </div>

            {mappings.length === 0 && !loadingMappings ? (
              <div className="text-center py-12 text-fg-2">
                <FiDatabase className="mx-auto mb-3 text-fg-3" size={48} />
                <p className="text-lg font-medium">No broker mappings found</p>
                <p className="text-sm mt-1">This instrument hasn't been synced from any broker yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {mappings.map((mapping, idx) => {
                  const key = mapping.mapping_id || mapping.broker_id || `broker-${idx}`;
                  const isExpanded = expandedBrokers[key];
                  const sourceData = mapping.broker_metadata || mapping.source_data || mapping.original_data;
                  
                  return (
                    <div key={key} className="transition-colors">
                      {/* Broker Header Row */}
                      <div 
                        className={`px-4 py-4 flex items-center gap-4 cursor-pointer hover:bg-bg-3 ${isExpanded ? 'bg-bg-3' : ''}`}
                        onClick={() => toggleBrokerExpanded(key)}
                      >
                        {/* Timeline Connector */}
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${mapping.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-bg-3 text-fg-2'}`}>
                            <FiDatabase size={18} />
                          </div>
                          {idx < mappings.length - 1 && (
                            <div className="w-0.5 h-8 bg-bg-3 mt-2"></div>
                          )}
                        </div>

                        {/* Broker Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-fg-1 text-lg">
                              {mapping.broker_name}
                            </span>
                            {mapping.is_active ? (
                              <span className="ds-badge profit xs">
                                Active
                              </span>
                            ) : (
                              <span className="ds-badge neutral xs">
                                Inactive
                              </span>
                            )}
                            {/* Processing Status Badge */}
                            {mapping.processing_status && (
                              <span className={`ds-badge xs ${
                                mapping.processing_status === 'processed' ? 'profit' :
                                mapping.processing_status === 'skipped' ? 'warn' :
                                mapping.processing_status === 'failed' ? 'loss' :
                                'info'
                              }`}>
                                {mapping.processing_status}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-6 mt-2 text-sm text-fg-2">
                            <div>
                              <span className="text-fg-2">Symbol:</span>
                              <span className="ml-1 font-mono font-medium text-fg-1">
                                {mapping.broker_symbol}
                              </span>
                            </div>
                            <div>
                              <span className="text-fg-2">Token:</span>
                              <span className="ml-1 font-mono font-medium text-fg-1">
                                {mapping.broker_token || '-'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FiClock size={12} />
                              <span className="text-fg-2">Last Updated:</span>
                              <span className="ml-1 text-fg-2">
                                {mapping.updated_at ? new Date(mapping.updated_at).toLocaleString() : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Expand Arrow */}
                        <div className={`text-fg-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          <FiChevronRight size={20} />
                        </div>
                      </div>

                      {/* Expanded Source Data */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pl-20 bg-bg-3/50">
                          <div className="bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)] overflow-hidden">
                            <div className="px-4 py-2 bg-bg-3 border-b border-[var(--divider)]">
                              <h4 className="text-sm font-medium text-fg-2">
                                Raw Source Data from {mapping.broker_name}
                              </h4>
                            </div>
                            {sourceData ? (
                              <div className="p-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                  {Object.entries(sourceData).filter(([key]) => 
                                    !['processing_status', 'processing_error'].includes(key)
                                  ).map(([key, value]) => (
                                    <div key={key} className="border-l-2 border-blue-200 dark:border-blue-800 pl-3">
                                      <div className="text-xs text-fg-2 uppercase tracking-wide">
                                        {key.replace(/_/g, ' ')}
                                      </div>
                                      <div className="text-sm font-mono text-fg-1 break-all">
                                        {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-')}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Raw JSON Toggle */}
                                <details className="mt-4">
                                  <summary className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
                                    View Raw JSON
                                  </summary>
                                  <pre className="mt-2 p-3 bg-bg-3 rounded text-xs overflow-x-auto text-fg-2">
                                    {JSON.stringify(sourceData, null, 2)}
                                  </pre>
                                </details>
                              </div>
                            ) : (
                              <div className="p-4 text-center text-fg-2">
                                No source data available for this broker mapping.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Comparison Table - Side by Side */}
          {mappings.length > 1 && (
            <div className="bg-white dark:bg-bg-1 rounded-lg border border-[var(--divider)]">
              <div className="px-4 py-3 border-b border-[var(--divider)] bg-bg-3">
                <h3 className="ds-h3 text-fg-1">
                  Side-by-Side Comparison
                </h3>
                <p className="text-sm text-fg-2 mt-1">
                  Compare key fields across all broker sources
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-bg-3">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider w-32">
                        Field
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider bg-green-50 dark:bg-green-900/20">
                        <div className="flex items-center gap-1">
                          <FiCheck className="text-green-600" size={14} />
                          Master Record
                        </div>
                      </th>
                      {mappings.map((mapping, idx) => (
                        <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                          {mapping.broker_name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-fg-2">Symbol</td>
                      <td className="px-4 py-3 text-sm font-mono bg-green-50 dark:bg-green-900/10 text-fg-1">{selectedInstrument.symbol}</td>
                      {mappings.map((m, idx) => (
                        <td key={idx} className="px-4 py-3 text-sm font-mono text-fg-2">
                          {m.broker_symbol}
                          {m.broker_symbol !== selectedInstrument.symbol && (
                            <span className="ml-1 text-yellow-600">≠</span>
                          )}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-fg-2">Token</td>
                      <td className="px-4 py-3 text-sm font-mono bg-green-50 dark:bg-green-900/10 text-fg-2">-</td>
                      {mappings.map((m, idx) => (
                        <td key={idx} className="px-4 py-3 text-sm font-mono text-fg-2">
                          {m.broker_token || '-'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-fg-2">Status</td>
                      <td className="px-4 py-3 text-sm bg-green-50 dark:bg-green-900/10">
                        <span className="ds-badge profit xs">
                          Active
                        </span>
                      </td>
                      {mappings.map((m, idx) => (
                        <td key={idx} className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            {m.is_active ? (
                              <span className="ds-badge profit xs">Active</span>
                            ) : (
                              <span className="ds-badge neutral xs">Inactive</span>
                            )}
                            {m.processing_status && (
                              <span className={`ds-badge xs ${
                                m.processing_status === 'processed' ? 'profit' :
                                m.processing_status === 'skipped' ? 'warn' :
                                m.processing_status === 'failed' ? 'loss' :
                                'info'
                              }`}>
                                {m.processing_status}
                              </span>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-medium text-fg-2">Last Updated</td>
                      <td className="px-4 py-3 text-sm bg-green-50 dark:bg-green-900/10 text-fg-2">
                        {selectedInstrument.updated_at ? new Date(selectedInstrument.updated_at).toLocaleString() : '-'}
                      </td>
                      {mappings.map((m, idx) => (
                        <td key={idx} className="px-4 py-3 text-sm text-fg-2">
                          {m.updated_at ? new Date(m.updated_at).toLocaleString() : '-'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {searchResults.length === 0 && !loading && !selectedInstrument && (
        <div className="text-center py-16 text-fg-2">
          <FiSearch className="mx-auto mb-4 text-fg-3" size={64} />
          <h3 className="ds-h3 text-fg-2">Search for an Instrument</h3>
          <p className="mt-2 text-fg-2 max-w-md mx-auto">
            Enter a symbol like RELIANCE, NIFTY, or TCS to see all matching instruments 
            and their broker-specific mappings.
          </p>
        </div>
      )}
    </div>
  );
};

export default BrokerMappingsTab;
